import { createContext, useContext, useState, useCallback } from 'react'
import {
  navScrollTo,
  pinElementToViewport,
  disableScrollSnap,
} from '../utils/unifiedScroll'

const NavigationContext = createContext(null)

/**
 * NavigationProvider - Zentrale Navigation-Logic für die gesamte App
 *
 * Single Source of Truth für:
 * - FAQ Accordion State (expandedFAQIndex)
 * - Navigation-Logik (scroll + accordions öffnen/schließen)
 *
 * Wird von DesktopNav und SidecarMenu genutzt (keine Duplizierung mehr)
 *
 * FAQ menu scroll (2026-08-11): do NOT use navScrollTo for #faq / #faq-N.
 * navScrollTo re-enables mandatory snap after 900ms; landing mid/near FAQ
 * then snaps to the section above. Soft-snap + pinElementToViewport (same
 * class as in-section FAQ open) is the correct path.
 */
export function NavigationProvider({ children }) {
  const [expandedFAQIndex, setExpandedFAQIndex] = useState(null)
  const [sidecarOpen, setSidecarOpen] = useState(false)
  const [isBurgerClosing, setIsBurgerClosing] = useState(false)
  const [headerVisible, setHeaderVisible] = useState(true)

  const navigateTo = useCallback((anchor) => {
    if (!anchor) return

    if (anchor.startsWith('#faq-')) {
      const index = parseInt(anchor.replace('#faq-', ''), 10)
      if (isNaN(index) || index < 0) return

      // Soft-snap hold before scroll (IO may not have fired yet from top of page)
      document.documentElement.setAttribute('data-faq-section-active', 'true')
      disableScrollSnap()
      setExpandedFAQIndex(index)

      // Wait for accordion expand (~500ms) then pin question with 300ms ease
      window.setTimeout(() => {
        const el = document.querySelector(anchor)
        if (el) pinElementToViewport(el, { offset: 80, duration: 800 })
      }, 520)
    } else if (anchor === '#faq') {
      setExpandedFAQIndex(null)
      document.documentElement.setAttribute('data-faq-section-active', 'true')
      disableScrollSnap()
      const el = document.querySelector(anchor)
      if (el) pinElementToViewport(el, { offset: 16, duration: 800 })
    } else {
      const el = document.querySelector(anchor)
      if (el) navScrollTo(el)
    }
  }, [])

  return (
    <NavigationContext.Provider value={{ navigateTo, expandedFAQIndex, setExpandedFAQIndex, sidecarOpen, setSidecarOpen, isBurgerClosing, setIsBurgerClosing, headerVisible, setHeaderVisible }}>
      {children}
    </NavigationContext.Provider>
  )
}

/**
 * useNavigation Hook
 *
 * Gibt Zugriff auf die Navigation-Funktionen, FAQ-Index und Sidecar State
 *
 * @returns {Object} { navigateTo, expandedFAQIndex, setExpandedFAQIndex, sidecarOpen, setSidecarOpen, isBurgerClosing, setIsBurgerClosing }
 */
export function useNavigation() {
  const context = useContext(NavigationContext)
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider')
  }
  return context
}
